import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { HttpRes } from '../../../models/common/http.model';
import { order_urls, membership_urls } from '../Constants/api-constants';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
  observe: 'response' as const,
};

export interface GetOrdersParams {
  pageSize?: number;
  page?: number;
  seasonId?: number | null;
  fetchType?: string;
  orderTypeId?: number;
  searchValue?: string;
  orderBy?: string | null;
  orderFor?: string | null;
  orderForName?: string | null;
  orderByName?: string | null;
  planId?: number | null;
  orderStatus?: string | null;
  invoiceGenerated?: boolean | null;
  verificationStatus?: string | null;
  refundStatus?: string | null;
  orderId?: number | null;
  serializeType?: string | null;
  eventId?: number | null;
  includeVerificationCounts?: boolean;
  includeRefunds?: boolean;
  fetchAll?: boolean;
}

export interface GetInvoicesParams {
  pageSize?: number;
  page?: number;
  seasonId?: number | null;
  fetchType?: string;
  paymentStatus?: string | null;
  searchValue?: string;
  orderForName?: string | null;
  orderByName?: string | null;
  paymentType?: string | null;
  refundStatus?: string | null;
  eventId?: number | null;
  invoiceType?: string;
  includeRefunds?: boolean;
  invoiceIds?: number | string;
}

export interface GetInvoiceSummariesParams {
  page?: number;
  pageSize?: number;
  fetchJsonData?: boolean;
  sortBy?: string;
  sortOrder?: string;
  paymentStatus?: string | null;
  searchValue?: string | null;
  eventId?: number | null;
  seasonId?: string | number | null;
  raisedToName?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {

  constructor(private readonly http: HttpClient) {}

  private readonly baseUrl = environment.apiUrl;
  private getSwitchUserRoleId(): string {
    if (!globalThis.window?.sessionStorage) {
      return '';
    }
    const raw =
      sessionStorage.getItem('switch_user_role_id') ||
      sessionStorage.getItem('switch_user_id') ||
      '';
    if (raw === '' || raw === '-1') {
      return '';
    }
    const n = Number.parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0) {
      return '';
    }
    return raw;
  }

  /**
   * Get orders with filters
   */
  get_orders(params: GetOrdersParams): Observable<any> {
    const httpParams = this.buildGetOrdersParams(params);

    return this.http.get(`${this.baseUrl}ott/v1/get_membership_orders`, { params: httpParams, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          success: response.status === 200,
          data: response.body?.data || response.body,
        };
      })
    );
  }

  private buildGetOrdersParams(params: GetOrdersParams): HttpParams {
    let httpParams = new HttpParams().set('include_details', 'true');
    httpParams = this.setOrderQueryParam(httpParams, 'per_page', params.pageSize);
    httpParams = this.setOrderQueryParam(httpParams, 'page_num', params.page);
    httpParams = this.setOrderQueryParam(httpParams, 'fetch_all', params.fetchAll ? 'true' : null);
    httpParams = this.setOrderQueryParam(httpParams, 'fetch_type', params.fetchType);
    httpParams = this.setOrderQueryParam(httpParams, 'order_type_id', params.orderTypeId);
    httpParams = this.setOrderQueryParam(httpParams, 'event_ids', params.eventId);
    httpParams = this.setOrderQueryParam(
      httpParams,
      'include_verification_counts',
      params.includeVerificationCounts ? 'true' : null,
    );
    httpParams = this.setOrderQueryParam(
      httpParams,
      'include_refunds',
      params.includeRefunds ? 'true' : null,
    );
    httpParams = this.setOrderQueryParam(httpParams, 'season_id', params.seasonId);
    httpParams = this.setOrderQueryParam(httpParams, 'search_value', params.searchValue);
    httpParams = this.setOrderQueryParam(httpParams, 'order_by', params.orderBy);
    httpParams = this.setOrderQueryParam(httpParams, 'order_for', params.orderFor);
    httpParams = this.setOrderQueryParam(httpParams, 'order_for_name', params.orderForName);
    httpParams = this.setOrderQueryParam(httpParams, 'order_by_name', params.orderByName);
    httpParams = this.setOrderQueryParam(httpParams, 'plan_id', params.planId);
    httpParams = this.setOrderQueryParam(
      httpParams,
      'order_status',
      params.orderStatus ? params.orderStatus.toUpperCase() : null,
    );
    if (params.invoiceGenerated !== null && params.invoiceGenerated !== undefined) {
      httpParams = httpParams.set(
        'invoice_generated',
        params.invoiceGenerated.toString(),
      );
    }
    httpParams = this.setOrderQueryParam(
      httpParams,
      'verification_status',
      params.verificationStatus,
    );
    httpParams = this.setOrderQueryParam(httpParams, 'refund_status', params.refundStatus);
    httpParams = this.setOrderQueryParam(httpParams, 'id', params.orderId);
    httpParams = this.setOrderQueryParam(httpParams, 'serialize_type', params.serializeType);

    const switchUserRoleId = this.getSwitchUserRoleId();
    if (switchUserRoleId) {
      httpParams = httpParams.set('switch_user_role_id', switchUserRoleId);
    }
    return httpParams;
  }

  private setOrderQueryParam(
    params: HttpParams,
    key: string,
    value: string | number | null | undefined,
  ): HttpParams {
    if (value == null || value === '') {
      return params;
    }
    return params.set(key, String(value));
  }

  /**
   * Get invoices with filters
   */
  get_invoices(params: GetInvoicesParams): Observable<any> {
    let httpParams = new HttpParams();

    if (params.pageSize) httpParams = httpParams.set('page_size', params.pageSize.toString());
    if (params.page) httpParams = httpParams.set('page', params.page.toString());
    if (params.seasonId) httpParams = httpParams.set('season_id', params.seasonId.toString());
    if (params.fetchType) httpParams = httpParams.set('fetch_type', params.fetchType);
    if (params.paymentStatus) httpParams = httpParams.set('payment_status', params.paymentStatus);
    if (params.searchValue) httpParams = httpParams.set('search_value', params.searchValue);
    if (params.orderForName) httpParams = httpParams.set('order_for_name', params.orderForName);
    if (params.orderByName) httpParams = httpParams.set('order_by_name', params.orderByName);
    if (params.paymentType) httpParams = httpParams.set('payment_type', params.paymentType);
    if (params.refundStatus) httpParams = httpParams.set('refund_status', params.refundStatus);
    if (params.eventId) httpParams = httpParams.set('event_ids', params.eventId.toString());
    if (params.invoiceType) httpParams = httpParams.set('invoice_type', params.invoiceType);
    if (params.includeRefunds) httpParams = httpParams.set('include_refunds', 'true');
    if (params.invoiceIds) {
      httpParams = httpParams.set('invoice_ids', params.invoiceIds.toString());
    }

    return this.http.get(`${this.baseUrl}invoice/v1/get_invoices`, { params: httpParams, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          success: response.status === 200,
          data: response.body?.data || response.body,
          count: response.body?.count || response.body?.total_count,
        };
      })
    );
  }

  /**
   * Create a single invoice
   */
  create_invoice(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}invoice/v1/create_invoice`, payload, httpOptions).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          success: response.status === 200 || response.status === 201,
          data: response.body,
        };
      })
    );
  }

  /**
   * Create bulk invoices
   */
  create_bulk_invoice(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}invoice/v1/create_bulk_invoice`, payload, httpOptions).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          success: response.status === 200 || response.status === 201,
          data: response.body,
        };
      })
    );
  }

  /**
   * Update order payment status
   */
  update_order_payment_status(orderId: number, status: string): Observable<any> {
    const payload = {
      order_id: orderId,
      order_status: status.toUpperCase()
    };

    return this.http.post(`${this.baseUrl}order/order/update_order_status`, payload, httpOptions).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          success: response.status === 200,
          data: response.body,
        };
      })
    );
  }

  /**
   * Get seasons with plans
   */
  get_seasons_with_plans(status: string = 'ongoing'): Observable<HttpRes> {
    let httpParams = new HttpParams();
    if (status) {
      httpParams = httpParams.set('status', status);
    }
    
    return this.http.get(`${this.baseUrl}ott/v1/get_seasons_with_plans`, { params: httpParams, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          success: response.status === 200,
          data: response.body,
        };
      })
    );
  }

  /**
   * Get plans
   */
  get_plans(): Observable<any> {
    return this.http.get(membership_urls.get_membership_plans, httpOptions).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          success: response.status === 200,
          data: response.body?.data || response.body,
        };
      })
    );
  }

  /**
   * Get order payment details
   */
  get_order_payment_details(orderId: number): Observable<any> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('order_id', orderId.toString());

    return this.http.get(order_urls.get_order_payment_details, { params: httpParams, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          success: response.status === 200,
          data: response.body?.data || response.body,
        };
      })
    );
  }

  /**
   * Get invoice summaries (for merged invoices)
   */
  get_invoice_summaries(options: GetInvoiceSummariesParams = {}): Observable<HttpRes> {
    const {
      page = 1,
      pageSize = 20,
      fetchJsonData = false,
      sortBy = 'created_at',
      sortOrder = 'desc',
      paymentStatus,
      searchValue,
      eventId,
      seasonId,
      raisedToName,
    } = options;

    let params = new HttpParams();
    params = params.set('fetch_json_data', fetchJsonData.toString());
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());
    params = params.set('sort_by', sortBy);
    params = params.set('sort_order', sortOrder);
    
    // Add optional filter parameters
    if (paymentStatus !== null && paymentStatus !== undefined && paymentStatus !== '') {
      params = params.set('payment_status', paymentStatus);
    }
    if (searchValue !== null && searchValue !== undefined && searchValue !== '') {
      params = params.set('search_value', searchValue);
    }
    if (raisedToName !== null && raisedToName !== undefined && raisedToName !== '') {
      params = params.set('raised_to_name', raisedToName);
    }
    if (eventId !== null && eventId !== undefined) {
      params = params.set('event_id', eventId.toString());
    }
    if (seasonId !== null && seasonId !== undefined && seasonId !== '') {
      params = params.set('season_id', seasonId.toString());
    }
    
    return this.http.get<HttpRes>(`${this.baseUrl}invoice-summary/v1/get_invoice_summaries`, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          code: response.status,
          msg: response.body.msg || 'Success',
          data: response.body.data,
          count: response.body.count || response.body.data?.length || 0
        } as any;
      })
    );
  }
}

